/**
 * Vocações de um município na camada `municipios_pe_adepe`.
 *
 * A planilha da ADEPE achata até cinco vocações em colunas numeradas
 * (`vocacao_1`, `classificacao_1`, …, `porte_5`). Aqui elas voltam a ser uma
 * lista, já com os campos que não acrescentam nada omitidos, para que o
 * tooltip e o modal de detalhe mostrem a mesma coisa.
 */

export const MAX_VOCACOES = 5;

/** Especialização que quase todos os municípios têm — mostrar só quando difere. */
const ESPECIALIZACAO_PADRAO = "muito alta";

/**
 * A ADEPE descreve a maturidade com vocabulário de APL, mas uma Vocação não é
 * um APL (ver CONTEXT.md). Valores fora deste mapa aparecem como vieram.
 */
const MATURIDADE_LABELS: Record<string, string> = {
  "apl em formacao": "Em formação",
  "apl consolidado": "Consolidada",
};

export interface Vocacao {
  rank: number;
  nome: string;
  maturidade: string | null;
  porte: string | null;
  /** Nulo quando repete o próprio nome da vocação. */
  produtos: string | null;
  /** Nulo quando é a especialização padrão ("Muito alta"). */
  especializacao: string | null;
}

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function text(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed === "" ? null : trimmed;
}

export function formatMaturidade(value: unknown): string | null {
  const raw = text(value);
  if (!raw) return null;
  return MATURIDADE_LABELS[normalize(raw)] ?? raw;
}

export function parseVocacoes(properties: Record<string, any>): Vocacao[] {
  const vocacoes: Vocacao[] = [];

  for (let rank = 1; rank <= MAX_VOCACOES; rank++) {
    const nome = text(properties[`vocacao_${rank}`]);
    if (!nome) continue;

    const produtos = text(properties[`produtos_principais_${rank}`]);
    const especializacao = text(
      properties[`estratificacao_especializacao_${rank}`]
    );

    vocacoes.push({
      rank,
      nome,
      maturidade: formatMaturidade(properties[`classificacao_${rank}`]),
      porte: text(properties[`porte_${rank}`]),
      produtos:
        produtos && normalize(produtos) !== normalize(nome) ? produtos : null,
      especializacao:
        especializacao && normalize(especializacao) !== ESPECIALIZACAO_PADRAO
          ? especializacao
          : null,
    });
  }

  return vocacoes;
}

/** Município sem vocação na planilha: fica só com o contorno, sem tooltip nem detalhe. */
export function hasVocacao(
  properties: Record<string, any> | undefined
): boolean {
  return !!properties && text(properties.vocacao_1) !== null;
}
